import { DataFetcher } from "components/DataFetcher";
import ListNavigation from "components/ListNavigation/ListNavigation";
import Pod from "components/Pod/Pod";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import "./CourseView.scss";
import CourseItem from "./components/CourseItem";
import useScrollToPageArea from "hooks/useScrollToPageArea";

const MODULE_ID_TEXT = "moduleId";
const COURSE_ID_TEXT = "courseId";

const CourseView = () => {
	const { hash } = useLocation();
	const fetchData = { url: "/api/v3/CoursesAndModules/ByCurrentUser" };
	const [data, setData] = useState(null);
	const [expandedModuleId, setExpandedModuleId] = useState(null);
	const [courseAndModuleData, setCourseAndModuleData] = useState(null);
	const [packageAndCourseData, setPackageAndCourseData] = useState(null);
	const { scroll } = useScrollToPageArea();
	const [scrollTarget, setScrollTarget] = useState(null); // Track scroll target
	const [pendingCourseIdToExpand, setPendingCourseIdToExpand] = useState(null); // Expand the first module of this course when ready


	/* Helper to convert the course array so they are nested inside packages */

	const buildPackagesFromCourses = (courses) => {
		const packageMap = new Map();
		console.log(courses);

		for (const rawCourse of courses) {
			const offerId = rawCourse.OfferId;
			const offerName = rawCourse.OfferName;

			const course = {
				...rawCourse,
				Id: rawCourse.Id,
				Name: rawCourse.Name,
			};

			if (!packageMap.has(offerId)) {
				packageMap.set(offerId, {
					Id: offerId,
					package: offerName,
					packageCourses: [],
				});
			}

			packageMap.get(offerId).packageCourses.push(course);
		}
		const packages = Array.from(packageMap.values());

		packages.forEach((pkg) => {
			pkg.packageCourses.sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0));
		});

		packages.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));

		return packages;
	};


	/*
	 * Effect for processing the returned data from the DataFetcher.
	 * This needs to be retained as a useEffect (instead of using a useCallback) - otherwise the scrolling doesn't work.
	 */
	useEffect(() => {
		if (data && !courseAndModuleData) {
			data.sort((a, b) => a.Position - b.Position);
			for (let item of data) {
				item.SubContent.sort((a, b) => a.Position - b.Position);
			}

			const nextPackages = buildPackagesFromCourses(data);
			setPackageAndCourseData(nextPackages);

			const nextCourses = nextPackages.flatMap((pkg) => pkg.packageCourses || []);
			setCourseAndModuleData(nextCourses);
		}
	}, [data, courseAndModuleData]);


	const parseIdFromHash = (hashValue, idText) => {
		const regex = new RegExp(`${idText}-(\\d+)`);
		const match = hashValue.match(regex);
		return match ? Number(match[1]) : null;
	};

	useEffect(() => {
		if (hash) {
			const element = hash.slice(1);
			setScrollTarget(element);

			const courseId = parseIdFromHash(element, COURSE_ID_TEXT);
			const moduleId = parseIdFromHash(element, MODULE_ID_TEXT);

			const feedbackInformation = { courseId, moduleId };
			sessionStorage.setItem("feedbackInformation", JSON.stringify(feedbackInformation));

			// Only expand the first module if this navigation did NOT come from ListNavigation
			const suppressExpand = sessionStorage.getItem("suppressExpand") === "1";

			if (courseId !== null && !suppressExpand) {
				setPendingCourseIdToExpand(courseId);
			}

			// Clear the one-shot flag so normal behavior resumes afterward
			if (suppressExpand) {
				sessionStorage.removeItem("suppressExpand");
			}
		}
	}, [hash]);


	// When navigating to a course (with or without a module in the URL), expand that course's first module.
	useEffect(() => {
		if (pendingCourseIdToExpand != null && Array.isArray(courseAndModuleData)) {
			const targetCourse = courseAndModuleData.find(
				(c) => (c.Id ?? c.id) === pendingCourseIdToExpand
			);
			const firstModuleId = targetCourse?.SubContent?.[0]?.Id ?? null;
			if (firstModuleId != null) {
				setExpandedModuleId(firstModuleId);
			}
			setPendingCourseIdToExpand(null);
		}
	}, [pendingCourseIdToExpand, courseAndModuleData]);

	// Effect for Scrolling.
	useEffect(() => {
		if (scrollTarget && courseAndModuleData) {
			scroll(scrollTarget);
			setScrollTarget(null);
		}
	}, [scrollTarget, courseAndModuleData, scroll]);

	const handleExpandModule = (moduleId) => {
		setExpandedModuleId((prev) => (prev === moduleId ? null : moduleId));
	};

	// Tracking scroll

	const [inViewCourseId, setInViewCourseId] = useState(null);
	const [inViewPackageId, setInViewPackageId] = useState(null);

	const courseIdToPackageId = useMemo(() => {
		if (!packageAndCourseData) return new Map();
		const map = new Map();
		packageAndCourseData.forEach((pkg) => {
			(pkg.packageCourses || []).forEach((course) => {
				const courseId = course.Id ?? course.id;
				const packageId = pkg.id ?? pkg.Id ?? null;
				map.set(courseId, packageId);
			});
		});
		return map;
	}, [packageAndCourseData]);

	const requestFrameRef = useRef(0);
	const lastSelectedCourseIdRef = useRef(null);

	const clampToRange = (value, min, max) => Math.min(Math.max(value, min), max);

	const selectMostVisibleCourse = useCallback(() => {
		requestFrameRef.current = 0;

		if (!Array.isArray(courseAndModuleData) || courseAndModuleData.length === 0) {
			return;
		}

		const viewHeight = window.innerHeight || 0;
		if (viewHeight <= 0) return;

		let mostProminent = {
			courseId: null,
			visibleHeight: -1,
			centerDistance: Number.POSITIVE_INFINITY,
		};

		for (let i = 0; i < courseAndModuleData.length; i++) {
			const course = courseAndModuleData[i];
			const courseId = course?.Id ?? course?.id;
			if (courseId == null) continue;

			const courseEl = document.getElementById(`courseId-${courseId}`);
			if (!courseEl) continue;

			const bounds = courseEl.getBoundingClientRect();

			const clampedTop = clampToRange(bounds.top, 0, viewHeight);
			const clampedBottom = clampToRange(bounds.bottom, 0, viewHeight);
			const visibleHeight = Math.max(0, clampedBottom - clampedTop);
			if (visibleHeight <= 0) continue;

			const elementCenterY = bounds.top + bounds.height / 2;
			const viewportCenterY = viewHeight / 2;
			const centerDistance = Math.abs(elementCenterY - viewportCenterY);

			const isBetterByHeight = visibleHeight > mostProminent.visibleHeight;
			const isTiedButCloserToCenter =
				visibleHeight === mostProminent.visibleHeight &&
				centerDistance < mostProminent.centerDistance;

			if (isBetterByHeight || isTiedButCloserToCenter) {
				mostProminent = { courseId, visibleHeight, centerDistance };
			}
		}

		let nextCourseId = mostProminent.courseId;
		if (nextCourseId == null) {
			if (inViewCourseId == null && courseAndModuleData.length > 0) {
				nextCourseId = courseAndModuleData[0].Id ?? courseAndModuleData[0].id ?? null;
			} else {
				nextCourseId = inViewCourseId;
			}
		}

		if (nextCourseId != null && lastSelectedCourseIdRef.current !== nextCourseId) {
			lastSelectedCourseIdRef.current = nextCourseId;
			setInViewCourseId(nextCourseId);
			setInViewPackageId(courseIdToPackageId.get(nextCourseId) ?? null);
		}
	}, [courseAndModuleData, courseIdToPackageId, inViewCourseId]);

	const scheduleVisibilityMeasure = useCallback(() => {
		if (requestFrameRef.current) return;
		requestFrameRef.current = requestAnimationFrame(selectMostVisibleCourse);
	}, [selectMostVisibleCourse]);

	useEffect(() => {
		const id = requestAnimationFrame(selectMostVisibleCourse);
		return () => cancelAnimationFrame(id);
	}, [courseAndModuleData, selectMostVisibleCourse]);

	useEffect(() => {
		window.addEventListener("scroll", scheduleVisibilityMeasure, { passive: true });
		window.addEventListener("resize", scheduleVisibilityMeasure);
		return () => {
			window.removeEventListener("scroll", scheduleVisibilityMeasure);
			window.removeEventListener("resize", scheduleVisibilityMeasure);
			if (requestFrameRef.current) cancelAnimationFrame(requestFrameRef.current);
			requestFrameRef.current = 0;
		};
	}, [scheduleVisibilityMeasure]);

	return (
		<DataFetcher fetchData={fetchData} setData={setData}>
			<div className="grid-two-col gutters">
				<Pod title="Courses">
					{courseAndModuleData && (
						<div className="list-view">
							{courseAndModuleData.map((course, index) => {
								return (
								<CourseItem
									key={course.Id}
									course={course}
									expandedModuleId={expandedModuleId}
									onExpand={handleExpandModule}
								/>)
							})}
						</div>
					)}
				</Pod>
				<Pod title="Course Navigation" mobileOrder={1} sticky>
					{courseAndModuleData && (
						<ListNavigation
							items={packageAndCourseData}
							packageInView={inViewPackageId}
							courseInView={inViewCourseId}
						/>
					)}
				</Pod>
			</div>
		</DataFetcher>
	);
};

export default CourseView;
